import { useState, type KeyboardEvent } from "react";
import { useLogin } from "../hooks/useLogin";
import cotagroLogoImg from "../../../assets/LOGO_TRIANGULOS_CHICOS.svg";
import { LockIcon, SpinnerIcon } from "../../../shared/icons";
import { PasswordInput } from "./PasswordInput";
import { ErrorBanner } from "./ErrorBanner";
import { RecoverModal } from "./RecoverModal";

interface LoginPageProps {
  auth: ReturnType<typeof useLogin>;
}

export function LoginPage({ auth }: LoginPageProps) {
  const {
    username,
    password,
    newPassword,
    confirmPassword,
    error,
    isLoading,
    step,
    setUsername,
    setPassword,
    setNewPassword,
    setConfirmPassword,
    handleLogin,
    handleNewPassword,
  } = auth;

  const [showRecover, setShowRecover] = useState(false);

  const onLoginKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLoading) handleLogin();
  };

  const onNewPassKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLoading) handleNewPassword();
  };

  /* ── Success Screen ── */
  if (step === "AUTHENTICATED") {
    return (
      <div className="login-page active">
        <div className="login-success-screen">
          <div className="login-success-check">
            <svg width="48" height="48" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="login-success-title">¡Bienvenido!</h2>
          <p className="login-success-subtitle">Sesión iniciada correctamente</p>
        </div>
      </div>
    );
  }

  /* ── New Password Required Screen ── */
  if (step === "NEW_PASSWORD") {
    return (
      <div className="login-page active" id="page-new-password">
        <div className="login-brand !mb-2">
          <img src={cotagroLogoImg} className="h-32 w-auto object-contain" alt="Cotagro" />
        </div>

        <div className="recover-icon-wrap" style={{ marginBottom: 20 }}>
          <LockIcon />
        </div>

        <h1 className="login-title" style={{ fontSize: 26 }}>Creá tu nueva contraseña</h1>
        <p className="login-subtitle">
          Por seguridad, necesitás cambiar tu contraseña la primera vez que ingresás.
        </p>

        <ErrorBanner message={error} />

        <div className="login-field">
          <PasswordInput
            value={newPassword}
            onChange={setNewPassword}
            onKeyDown={onNewPassKeyDown}
            placeholder="Nueva contraseña"
            id="newPass"
            autoComplete="new-password"
            disabled={isLoading}
            autoFocus
          />
        </div>

        <div className="login-field">
          <PasswordInput
            value={confirmPassword}
            onChange={setConfirmPassword}
            onKeyDown={onNewPassKeyDown}
            placeholder="Confirmar contraseña"
            id="confirmPass"
            autoComplete="new-password"
            disabled={isLoading}
          />
        </div>

        <div style={{ fontSize: 12, color: "#9E9E9E", marginBottom: 24, paddingLeft: 4 }}>
          Mínimo 8 caracteres, 1 mayúscula, 1 minúscula y 1 número.
        </div>

        <button className="login-btn" onClick={handleNewPassword} disabled={isLoading} id="newPassBtn">
          {isLoading ? (
            <span className="login-btn-loading">
              <SpinnerIcon />
              Guardando...
            </span>
          ) : (
            "Confirmar contraseña"
          )}
        </button>

        <div className="login-footer">Cotagro · Cooperativa Agropecuaria Limitada</div>
      </div>
    );
  }

  /* ── Login Screen ── */
  return (
    <>
      <div className="login-page active" id="page-login">
        <div className="login-brand !mb-2">
          <img src={cotagroLogoImg} className="h-32 w-auto object-contain" alt="Cotagro" />
        </div>

        <h1 className="login-title">Bienvenido</h1>
        <p className="login-subtitle">Ingresá a tu cuenta para continuar</p>

        <ErrorBanner message={error} />

        <div className="login-field">
          <input
            type="text"
            className="login-input"
            placeholder="Email"
            id="loginUser"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={onLoginKeyDown}
            disabled={isLoading}
            autoFocus
          />
        </div>

        <div className="login-field">
          <PasswordInput
            value={password}
            onChange={setPassword}
            onKeyDown={onLoginKeyDown}
            placeholder="Contraseña"
            id="loginPass"
            autoComplete="current-password"
            disabled={isLoading}
          />
        </div>

        <a className="login-forgot" onClick={() => setShowRecover(true)}>
          ¿Olvidaste tu contraseña?
        </a>

        <button className="login-btn" onClick={handleLogin} disabled={isLoading} id="loginBtn">
          {isLoading ? (
            <span className="login-btn-loading">
              <SpinnerIcon />
              Ingresando...
            </span>
          ) : (
            "Ingresar"
          )}
        </button>

        <div className="login-footer">Cotagro · Cooperativa Agropecuaria Limitada</div>
      </div>

      <RecoverModal isOpen={showRecover} onClose={() => setShowRecover(false)} />
    </>
  );
}
